import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Toolbar } from "primereact/toolbar";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import SessionAddEdit from "../SessionAddEdit/Index";
import { useAuthStore } from "../../../store/AuthStore";
import { useSchoolStore } from "../../../store/SchoolStore";
import utils from "../../../utils";
import constants from "../../../constants";
import { getRole } from "../../../apis/Utilities";
import { deleteRole, deleteRoles, getRoles } from "../../../apis/Role";
import RoleAddEdit from "../RoleAddEdit/Index";

const RoleList = ({ toast }) => {

  const [state] = useAuthStore();
  const [{ schoolId }] = useSchoolStore();

  let emptyRole = {
    id: null,
    schoolId: schoolId,
    name: "",
    createdBy: state.email,
    modifiedBy: state.email,
  };

  const [roles, setRoles] = useState(null);
  const [roleDialog, setRoleDialog] = useState(false);
  const [deleteRoleDialog, setDeleteRoleDialog] = useState(false);
  const [deleteRolesDialog, setDeleteRolesDialog] = useState(false);
  const [role, setRole] = useState(emptyRole);
  const [selectedRoles, setSelectedRoles] = useState(null);
  const [, setSubmitted] = useState(false);
  const [globalFilter, setGlobalFilter] = useState(null);
  const toastRL = useRef(toast);
  const dt = useRef(null);

  async function fetchRolesData() {
    var res = await getRoles(schoolId);
    setRoles(res.data);
  }

  useEffect(() => {
    fetchRolesData();
  }, [roleDialog, deleteRoleDialog, deleteRolesDialog, globalFilter]);

  const openNew = () => {
    setRole(emptyRole);
    setSubmitted(false);
    setRoleDialog(true);
  };

  const confirmDeleteSelected = () => {
    setDeleteRolesDialog(true);
  };

  const leftToolbarTemplate = () => {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          label="New"
          icon="pi pi-plus"
          severity="success"
          onClick={openNew}
        />
        <Button
          label="Delete"
          icon="pi pi-trash"
          severity="danger"
          onClick={confirmDeleteSelected}
          disabled={!selectedRoles || !selectedRoles.length}
        />
      </div>
    );
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const rightToolbarTemplate = () => {
    return (
      <Button
        label="Export"
        icon="pi pi-upload"
        className="p-button-help"
        onClick={exportCSV}
      />
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <Button
          icon="pi pi-pencil"
          rounded
          outlined
          className="mr-2"
          onClick={() => editRole(rowData)}
        />
        <Button
          icon="pi pi-trash"
          rounded
          outlined
          severity="danger"
          onClick={() => confirmDeleteRole(rowData)}
        />
      </React.Fragment>
    );
  };

  const editRole = (sessionData) => {
    setRole({ ...sessionData });
    setRoleDialog(true);
  };

  const confirmDeleteRole = (session) => {
    setRole(session);
    setDeleteRoleDialog(true);
  };

  const hideDeleteRoleDialog = () => {
    setDeleteRoleDialog(false);
  };

  const hideDeleteRolesDialog = () => {
    setDeleteRolesDialog(false);
  };

  const deleteRoleFunc = async () => {
    let resp = {
      data: null,
      responseCode: "",
      responseCodeDescription: "",
    };

    try {
      if (role && role.id) {
        resp = await deleteRole(role.id);
        setDeleteRoleDialog(false);
        setRole(emptyRole);
        toastRL.current.show(
          utils.toastCallback({
            severity: "success",
            summary: "Successful",
            detail: resp.responseCodeDescription,
          })
        );
      }
    } catch (error) {
      if (error.response) {
        resp = error.response;

        toastRL.current.show(
          utils.toastCallback({
            severity: "error",
            summary: "Submit Error",
            detail: resp.responseCodeDescription,
          })
        );
      } else {
        toastRL.current.show(
          utils.toastCallback({
            severity: "error",
            summary: "Network Error",
            detail: constants.networkErrorMessage,
          })
        );
      }
    }
  };

  const deleteSelectedRoles = async () => {
    let resp = {
      data: null,
      responseCode: "",
      responseCodeDescription: "",
    };

    try {
      let _sessions = roles.filter((val) => !selectedRoles.includes(val));
      resp = await deleteRoles(_sessions);
      setDeleteRolesDialog(false);
      setSelectedRoles(null);
      toastRL.current.show(
        utils.toastCallback({
          severity: "success",
          summary: "Successful",
          detail: resp.responseCodeDescription,
        })
      );
    } catch (error) {
      if (error.response) {
        resp = error.response;

        toastRL.current.show(
          utils.toastCallback({
            severity: "error",
            summary: "Submit Error",
            detail: resp.responseCodeDescription,
          })
        );
      } else {
        toastRL.current.show(
          utils.toastCallback({
            severity: "error",
            summary: "Network Error",
            detail: constants.networkErrorMessage,
          })
        );
      }
    }
  };

  const deleteRoleDialogFooter = (
    <React.Fragment>
      <Button
        label="No"
        icon="pi pi-times"
        outlined
        onClick={hideDeleteRoleDialog}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        severity="danger"
        onClick={async () => await deleteRoleFunc()}
      />
    </React.Fragment>
  );

  const deleteRolesDialogFooter = (
    <React.Fragment>
      <Button
        label="No"
        icon="pi pi-times"
        outlined
        onClick={hideDeleteRolesDialog}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        severity="danger"
        onClick={deleteSelectedRoles}
      />
    </React.Fragment>
  );

  const header = (
    <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
      <h4 className="m-0">Manage Role</h4>
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          type="search"
          onInput={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
        />
      </span>
    </div>
  );

  return (
    <div>
      <Toast ref={toastRL} />
      <div className="card">
        <Toolbar
          className="mb-4"
          left={leftToolbarTemplate}
          right={rightToolbarTemplate}
        ></Toolbar>
        <DataTable
          ref={dt}
          value={roles}
          selection={selectedRoles}
          onSelectionChange={(e) => setSelectedRoles(e.value)}
          dataKey="id"
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} sessions"
          globalFilter={globalFilter}
          header={header}
        >
          <Column selectionMode="multiple" exportable={false}></Column>
          <Column
            field="name"
            header="Name"
            sortable
            style={{ minWidth: "16rem" }}
          ></Column>
          <Column
            body={actionBodyTemplate}
            exportable={false}
            style={{ minWidth: "12rem" }}
          ></Column>
        </DataTable>
      </div>

      <Dialog
        visible={deleteRoleDialog}
        style={{ width: "32rem" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={deleteRoleDialogFooter}
        onHide={hideDeleteRoleDialog}
      >
        <div className="confirmation-content">
          <i
            className="pi pi-exclamation-triangle mr-3"
            style={{ fontSize: "2rem" }}
          />
          {role && (
            <span>
              Are you sure you want to delete <b>{role.name}</b>?
            </span>
          )}
        </div>
      </Dialog>

      <Dialog
        visible={deleteRolesDialog}
        style={{ width: "32rem" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={deleteRolesDialogFooter}
        onHide={hideDeleteRolesDialog}
      >
        <div className="confirmation-content">
          <i
            className="pi pi-exclamation-triangle mr-3"
            style={{ fontSize: "2rem" }}
          />
          {role && (
            <span>Are you sure you want to delete the selected roles?</span>
          )}
        </div>
      </Dialog>

      <RoleAddEdit
        toast={toastRL}
        roleAE={role}
        roleDialog={roleDialog}
        setRoleDialog={setRoleDialog}
      />
    </div>
  );
};

export default RoleList;
